import type { TaskRecord } from "@nexus/contracts";

import { isPersonaSetGenerationTask } from "@/features/task-progress/lib/is-persona-opportunity-sets-task";

/** Subtask `metadata.outcome` written by the generate-set worker. */
export const PERSONA_SET_GENERATION_OUTCOME = {
  NO_OPPORTUNITIES: "NO_OPPORTUNITIES",
  INDUSTRY_NOT_FOUND: "INDUSTRY_NOT_FOUND",
} as const;

export function readPersonaSetGenerationOutcome(task: TaskRecord): string | undefined {
  for (const stage of task.stages) {
    for (const subtask of stage.subtasks) {
      const outcome = subtask.metadata?.outcome;
      if (typeof outcome === "string" && outcome.length > 0) {
        return outcome;
      }
    }
  }
  return undefined;
}

function hasIncompleteProgress(task: TaskRecord): boolean {
  return task.stages.some((stage) =>
    stage.subtasks.some((subtask) => subtask.progress < 1),
  );
}

const cancelledPersonaSetGenerationIds = new Set<string>();

/** Stop asked the worker to cancel. Hub counts wait for the later soft-delete. */
export function markPersonaSetGenerationCancelled(correlationId: string): void {
  cancelledPersonaSetGenerationIds.add(correlationId);
}

export function unmarkPersonaSetGenerationCancelled(correlationId: string): void {
  cancelledPersonaSetGenerationIds.delete(correlationId);
}

/** True once, so a later real finish of the same id can still refresh the hub. */
export function consumePersonaSetGenerationCancelled(correlationId: string): boolean {
  return cancelledPersonaSetGenerationIds.delete(correlationId);
}

/** Correlation ids whose generate-set task is still in progress. */
export function inProgressPersonaSetGenerationIds(
  tasks: readonly TaskRecord[],
): Set<string> {
  const ids = new Set<string>();
  for (const task of tasks) {
    if (isPersonaSetGenerationTask(task) && hasIncompleteProgress(task)) {
      ids.add(task.correlationId);
    }
  }
  return ids;
}

/**
 * Ids that were in progress last poll and are no longer (progress 1, or the
 * task left the active list). First sight of an already-complete card is not
 * a finish — that would refetch on every page load with a leftover sidebar card.
 */
export function detectFinishedPersonaSetGenerationIds(
  previouslyInProgress: ReadonlySet<string>,
  tasks: readonly TaskRecord[],
): { inProgress: Set<string>; finishedIds: string[] } {
  const inProgress = inProgressPersonaSetGenerationIds(tasks);
  const finishedIds: string[] = [];
  for (const id of previouslyInProgress) {
    if (!inProgress.has(id)) {
      finishedIds.push(id);
    }
  }
  return { inProgress, finishedIds };
}
